import React, {useEffect, memo, useState} from 'react';
import {Stack} from '@mui/material';
import cloud from 'd3-cloud';

const WordCloud = ({width = 462, height = 250, wordList = []}) => {
  const [layoutWords, setLayoutWords] = useState([]);

  const minFontSize = 4;
  const maxFontSize = 28;
  const minShrinkFactor = 0.6; // Don't shrink below 60% of original size

  const sizes = wordList.map(d => d.size);
  const minSize = Math.min(...sizes);
  const maxSize = Math.max(...sizes);

  // Optional: truncate overly long words (visual only)
  function truncateText(text, maxChars = 30) {
    return text.length > maxChars ? text.slice(0, maxChars) + '..' : text;
  }

  function getFontSize(d) {
    // 1. Normalize importance (size)
    const normalized = (d.size - minSize) / (maxSize - minSize || 1);

    // 2. Get base font size from importance
    const baseSize = minFontSize + normalized * (maxFontSize - minFontSize);

    // 3. Estimate how wide this word would be
    const estimatedWidth = d.text.length * baseSize * 0.6;

    // 4. Add a shrink buffer based on importance (more important = more room)
    const importanceBoost = 1 + normalized * 1.5;

    // 5. Calculate shrink factor, bounded by minShrinkFactor
    const rawShrink = width / estimatedWidth;
    const shrinkFactor = Math.max(
      minShrinkFactor,
      Math.min(1, rawShrink * importanceBoost)
    );

    // 6. Final font size
    return baseSize * shrinkFactor;
  }

  useEffect(
    () => {
      const layout = cloud()
        .size([width, height])
        .spiral('archimedean')
        .words(
          wordList.map(d => ({
            ...d,
            text: truncateText(d.text), // optional visual trimming
          }))
        )
        .padding(1)
        .rotate(() => 0)
        .font('Impact')
        .fontSize(getFontSize)
        .on('end', setLayoutWords);
      layout.start();
    },
    [wordList, width, height]
  );

  return (
    <Stack alignItems="center" justifyContent="center">
      <svg
        width={width}
        height={height}
        style={{objectFit: 'cover', overflow: 'visible'}}>
        <g transform={`translate(${width / 2 - 5}, ${height / 2})`}>
          {layoutWords.map((word, i) => (
            <text
              key={i}
              fontFamily="Inter"
              textAnchor="middle"
              fontSize={word.size}
              style={{
                fontWeight: 500,
                textTransform: 'capitalize',
              }}
              fill={`rgb(${Math.floor(Math.random() * 255)},74,172)`}
              transform={`translate(${word.x}, ${word.y}) rotate(${
                word.rotate
              })`}>
              {word.text}
            </text>
          ))}
        </g>
      </svg>
    </Stack>
  );
};

export default memo(WordCloud);
